import { Component, Inject, OnInit } from '@angular/core';
import { AttributesValueType, CompanyAttributeDTO } from '@iota/iota-management-portal-v4-client';
import { IotaModalOptions, IotaModalSize, MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@iota/iota-ui-components';
import { IFileData, SheetFileToJsonService } from 'src/app/shared/services/sheet-file-to-json.service';
import { ATTRIBUTE_EXPORT_SEPARATOR, jsonStringifyArrayAndConcatenate } from '../../utils';

export interface ImportAttributesResult {
    deleteOldAttributes?: boolean;
    attributes: CompanyAttributeDTO[];
}

@Component({
    standalone: false,
    selector: 'app-dialog-import-attributes',
    templateUrl: './dialog-import-attributes.component.html',
    styleUrls: ['./dialog-import-attributes.component.scss']
})
export class DialogImportAttributesComponent implements OnInit {
    public modalOptions: Partial<IotaModalOptions> = {
        size: IotaModalSize.MEDIUM,
        showToggleFullScreenButton: false
    };

    public readonly newAttributesColumns: string[] = ['attributeName', 'valueType', 'temporary', 'locale', 'value'];

    loading = false;
    public currentStep: ImportAttributesStep;
    public uploadedFile: IFileData;
    public errorMessage: string;
    public infoMessage: string;

    public isHeaderSelectionFormValid: boolean;
    public newAttributesDataSource: Array<CompanyAttributeDTO>;

    public deleteOldAttributes: boolean;

    constructor(
        public readonly dialog: MatDialog,
        public readonly dialogRef: MatDialogRef<DialogImportAttributesComponent>,
        @Inject(MAT_DIALOG_DATA) public readonly data: any
    ) {}

    ngOnInit(): void {
        this.setStep(ImportAttributesStep.FileUpload);
    }

    public async onFileUploaded(files: File[]): Promise<void> {
        if (files == null || files.length === 0) {
            this.resetFileUpload();
            return;
        }

        this.errorMessage = null;
        let fileData: IFileData;
        try {
            fileData = await SheetFileToJsonService.parseFile(files[0]);
        } catch (error) {
            this.errorMessage = error.message;
            return;
        }

        const validationError = this.validateFileData(fileData);

        if (validationError != null) {
            this.errorMessage = validationError;
            return;
        }

        this.uploadedFile = fileData;
    }

    public confirmFileUpload(): void {
        this.validateParseData();
        this.setStep(ImportAttributesStep.Confirmation);
    }

    public getAttributeHighlight(overwritten: boolean, updated: boolean): string {
        if (overwritten) {
            return 'bg-error';
        }

        if (updated) {
            return 'bg-warning';
        }

        return '';
    }

    public async validateParseData(): Promise<void> {
        this.errorMessage = null;

        this.newAttributesDataSource = this.mapFileDataToAttributes(this.uploadedFile.data);

        if (this.newAttributesDataSource.length === 0) {
            this.errorMessage = $localize`The file contains no entries to import`;
        }

        this.setStep(ImportAttributesStep.Confirmation);
    }

    private mapFileDataToAttributes(fileData: object[]): CompanyAttributeDTO[] {
        const newAttributes: CompanyAttributeDTO[] = [];

        fileData.forEach((value) => {
            const attribute: CompanyAttributeDTO = {
                attributeName: value['attributeName'],
                valueType: value['valueType'],
                temporary: value['temporary'],
                locale: value['locale'],
                booleanValue: this.parseValueArray(value['booleanValue']),
                dateTimeValue: this.parseValueArray(value['dateTimeValue']),
                dateValue: this.parseValueArray(value['dateValue']),
                intValue: this.parseValueArray(value['intValue']),
                numValue: this.parseValueArray(value['numValue']),
                strValue: this.parseValueArray(value['strValue']),
                uuidValue: this.parseValueArray(value['uuidValue'])
            };

            newAttributes.push(attribute);
        });

        return newAttributes;
    }

    private parseValueArray(jsonString: string): any[] {
        if (jsonString) {
            if (jsonString.indexOf(';') > 0) {
                return jsonString.split(';').map((value) => JSON.parse(value));
            }
            return [JSON.parse(jsonString)];
        }
        return [];
    }

    private resetFileUpload(): void {
        this.currentStep = ImportAttributesStep.FileUpload;
        this.uploadedFile = null;
        this.errorMessage = null;
    }

    /**
     * Validate the parsed file, return error in case of any error
     * @param fileData Parsed file data
     */
    private validateFileData(fileData: IFileData): string {
        if (fileData.headers.length === 0) {
            return $localize`The file does not have any headers and therefore cannot be uploaded.`;
        }

        if (fileData.data.length === 0) {
            return $localize`The file is empty and therefore cannot be uploaded.`;
        }

        return null;
    }

    public getValueDisplayString(attribute: CompanyAttributeDTO): string {
        if (attribute.valueType === AttributesValueType.BOOL) {
            return jsonStringifyArrayAndConcatenate(attribute.booleanValue, ATTRIBUTE_EXPORT_SEPARATOR);
        }
        if (attribute.valueType === AttributesValueType.DATE) {
            return jsonStringifyArrayAndConcatenate(
                attribute.dateTimeValue.map((date) => new Date(date)),
                ATTRIBUTE_EXPORT_SEPARATOR
            );
        }
        if (attribute.valueType === AttributesValueType.DATETIME) {
            return jsonStringifyArrayAndConcatenate(
                attribute.dateValue.map((date) => new Date(date)),
                ATTRIBUTE_EXPORT_SEPARATOR
            );
        }
        if (attribute.valueType === AttributesValueType.INTEGER) {
            return jsonStringifyArrayAndConcatenate(attribute.intValue, ATTRIBUTE_EXPORT_SEPARATOR);
        }
        if (attribute.valueType === AttributesValueType.NUMBER) {
            return jsonStringifyArrayAndConcatenate(attribute.numValue, ATTRIBUTE_EXPORT_SEPARATOR);
        }
        if (attribute.valueType === AttributesValueType.STRING) {
            return jsonStringifyArrayAndConcatenate(attribute.strValue, ATTRIBUTE_EXPORT_SEPARATOR);
        }
        if (attribute.valueType === AttributesValueType.UUID) {
            return jsonStringifyArrayAndConcatenate(attribute.uuidValue, ATTRIBUTE_EXPORT_SEPARATOR);
        }
    }

    public confirmImport() {
        const result: ImportAttributesResult = {
            attributes: this.newAttributesDataSource,
            deleteOldAttributes: this.deleteOldAttributes
        };
        this.dialogRef.close(result);
    }

    close() {
        this.dialogRef.close();
    }

    private setStep(step: ImportAttributesStep): void {
        switch (step) {
            case ImportAttributesStep.FileUpload:
                this.errorMessage = null;
                this.uploadedFile = null;
                this.infoMessage = null;
                this.currentStep = ImportAttributesStep.FileUpload;
                break;
            case ImportAttributesStep.Confirmation:
                this.infoMessage = null;
                this.currentStep = ImportAttributesStep.Confirmation;
                break;
        }
    }
}

enum ImportAttributesStep {
    FileUpload = 1,
    Confirmation = 2
}
